import type { FeatureFlagKey, FeatureFlagSnapshot } from "./feature-flags";

/**
 * Structured local event log. R1 Step 15.
 *
 * Doc 02 section 4: every catch block either recovers visibly or surfaces, and
 * a swallowed exception is a defect. This is where a surfaced one goes.
 *
 * Entirely local. Nothing here is ever sent anywhere; the export is written to
 * the device as a file the trainee sends however he likes.
 */
/**
 * A detail value.
 *
 * Numbers, booleans and null carry no content. A string field may only hold
 * an identifier the application itself produced — a rule name, a node path,
 * a field type, a hash, a refusal code — never a line of a document and
 * never anything the trainee typed. Ruling 50 of 7 October 2026: a
 * diagnostic that carries his course material is a hard rule 2 violation in
 * the one file most likely to leave the device.
 */
export type LogValue = boolean | number | string | null;

export type LogDetail = Readonly<Record<string, LogValue>>;

export interface LogEvent {
  readonly action: string;
  /**
   * The shape of what happened, never its value.
   *
   * Ruling 50 requires every classification decision to record the blank,
   * its width, its line, the rule that fired and that rule's reason. There
   * was nowhere to put any of it, so nothing was recorded and every defect
   * had to be diagnosed by the trainee retyping a panel.
   */
  readonly detail: LogDetail;
  readonly message: string;
  readonly module: string;
  readonly schemaVersion: 1;
  readonly stack: string | null;
  readonly at: string;
}

/** Doc 02 section 4: the export carries the last fifty entries. */
export const EVENT_LOG_CAPACITY = 50;

/**
 * One timed moment of the session. Ruling 84 of 16 October 2026 (a).
 *
 * The name is one of the ten the ruling lists and the detail carries shape
 * only — a node path, a kind, a count. **Never an answer.** The ruling says
 * so in its own last sentence, and the questions phase is the one part of
 * the application that holds anything he wrote.
 */
export interface SessionMoment {
  readonly at: string;
  readonly detail: LogDetail;
  readonly name: string;
}

/**
 * How many moments the timeline holds.
 *
 * A delivery reaches eleven or so headings and he may pause a great many
 * times, so five hundred is far more than a session produces. It is bounded
 * all the same, because this runs on a phone.
 *
 * **The first moment is never dropped.** A ring that evicts the oldest would
 * discard `delivery-start`, and every other time in the file is read against
 * it; a timeline that cannot say when the session began is the defect Ruling
 * 84 exists to fix. So the oldest moment *after* the first goes instead, and
 * the report carries a count of how many went.
 */
export const SESSION_TIMELINE_CAPACITY = 500;

/**
 * How many classification decisions the export carries.
 *
 * Found 12 October 2026 in the trainee's own export. Doc 02 section 4 caps
 * the event log at fifty entries, and Ruling 50 requires **every**
 * classification decision recorded. Those pull against each other: loading
 * two documents produced 46 `blank-typed` events, which filled the ring and
 * evicted the calibration events the log was built to capture. The fifty
 * entries it did hold spanned 1.5 seconds and said nothing about the fault
 * he was reporting.
 *
 * So decisions go in their own bounded channel. The event log keeps its
 * fifty operational entries; the decisions sit beside them, and neither
 * crowds the other out.
 */
export const CLASSIFICATION_LIMIT = 400;

export interface EventLog {
  /** Record one classification decision. Ruling 50. */
  classify(detail: LogDetail): void;
  /** The decisions held, oldest first. */
  classifications(): readonly LogDetail[];
  /**
   * `detail` and `stack` are both optional: a routine event has no stack,
   * and only a classification decision or a refusal carries detail.
   */
  append(
    entry: Omit<LogEvent, "at" | "detail" | "schemaVersion" | "stack"> & {
      readonly detail?: LogDetail;
      readonly stack?: string | null;
    },
  ): void;
  clear(): void;
  entries(): readonly LogEvent[];
  /** How many moments were dropped to stay inside the bound. Ruling 84. */
  droppedMoments(): number;
  /** Record one timed moment of the session. Ruling 84. */
  moment(name: string, detail?: LogDetail): void;
  /** The moments held, oldest first. */
  moments(): readonly SessionMoment[];
}

/**
 * A bounded ring. The oldest entry is dropped once capacity is reached, so a
 * long session cannot grow without limit on a phone.
 */
export function createEventLog(
  now: () => string,
  capacity: number = EVENT_LOG_CAPACITY,
): EventLog {
  if (!Number.isInteger(capacity) || capacity < 1) {
    throw new RangeError("event log capacity must be a positive integer");
  }
  let held: LogEvent[] = [];
  let decisions: LogDetail[] = [];
  let timeline: SessionMoment[] = [];
  let dropped = 0;

  return {
    droppedMoments: () => dropped,
    moment: (name, detail) => {
      timeline.push({ at: now(), detail: detail ?? {}, name });
      if (timeline.length > SESSION_TIMELINE_CAPACITY) {
        // The first stays; the second-oldest goes. See the capacity's note.
        timeline.splice(1, 1);
        dropped += 1;
      }
    },
    moments: () => [...timeline],
    classify: (detail) => {
      decisions.push(detail);
      if (decisions.length > CLASSIFICATION_LIMIT) {
        decisions = decisions.slice(decisions.length - CLASSIFICATION_LIMIT);
      }
    },
    classifications: () => [...decisions],
    append: (entry) => {
      held.push({
        ...entry,
        detail: entry.detail ?? {},
        stack: entry.stack ?? null,
        schemaVersion: 1,
        at: now(),
      });
      if (held.length > capacity) held = held.slice(held.length - capacity);
    },
    clear: () => {
      held = [];
      decisions = [];
      timeline = [];
      dropped = 0;
    },
    entries: () => [...held],
  };
}

/** Turns an unknown thrown value into a loggable entry without losing it. */
/**
 * The one log for the running application.
 *
 * The shell owns the diagnostics band and the export; the modules are what
 * have something to record. The module registry mounts a module with nothing
 * but an element, so rather than thread a log through every signature there
 * is one instance, created on first use. Tests build their own with
 * `createEventLog` and never touch this.
 */
let shared: EventLog | null = null;

export function sharedEventLog(): EventLog {
  shared ??= createEventLog(() => new Date().toISOString());
  return shared;
}

/** Test seam: forget the shared log so one test cannot bleed into another. */
export function resetSharedEventLog(): void {
  shared = null;
}

/**
 * The hash of the most recently loaded file, read back out of the log.
 *
 * The shell's diagnostics band has to report an active file hash and the
 * shell does not load files; the modules do, and they record it. Reading it
 * back from the log avoids threading a second channel for one string.
 */
export function activeFileHashFromLog(log: EventLog): string | null {
  for (const event of [...log.entries()].reverse()) {
    const { fileHash: hash } = event.detail;
    if (typeof hash === "string" && hash !== "") return hash;
  }
  return null;
}

export function describeFailure(
  module: string,
  action: string,
  failure: unknown,
): Omit<LogEvent, "at" | "schemaVersion"> {
  if (failure instanceof Error) {
    return {
      module,
      action,
      message: failure.message,
      stack: failure.stack ?? null,
      detail: {},
    };
  }
  return {
    module,
    action,
    message: typeof failure === "string" ? failure : JSON.stringify(failure),
    stack: null,
    detail: {},
  };
}

export interface BugReportState {
  readonly activeFileHash: string | null;
  readonly appVersion: string;
  readonly featureFlags: FeatureFlagSnapshot;
  /**
   * How many **records** this device holds of each type. Never the keys: an
   * identity can carry order-set text. Ruling 50, and the leak of 8 October
   * 2026.
   *
   * Named in full since 16 October 2026, and the fault was mine. He read
   * `storedRecords: {"event": 1}` beside an export holding seven events and
   * reasonably asked which number was wrong. Neither: the whole event log
   * persists as **one** rolling record, so one record held seven events. The
   * count was right and its name read as a count of events. The counts of
   * what the report actually carries now sit beside it, so the question
   * cannot be asked a second time.
   */
  readonly storedRecordsByType: Readonly<Record<string, number>>;
}

export interface BugReport {
  /**
   * 3 since 16 October 2026: Ruling 84's timeline arrived, and
   * `storedRecords` was renamed to say what it counts.
   */
  readonly schemaVersion: 3;
  readonly capturedAt: string;
  /**
   * What this file carries, counted.
   *
   * Three channels of different shapes, each bounded differently, is three
   * chances to mistake one bound for another — which is exactly what
   * happened on 16 October 2026. So the file says how much of each it holds,
   * and `momentsDropped` says whether the timeline is the whole session or
   * only its start and its end.
   */
  readonly held: {
    readonly classifications: number;
    readonly events: number;
    readonly moments: number;
    readonly momentsDropped: number;
  };
  /**
   * Every blank's typing decision, in its own channel.
   *
   * Separate from `events` since 12 October 2026, because 46 of them filled
   * the fifty-entry event log and evicted everything else.
   */
  readonly classifications: readonly LogDetail[];
  readonly events: readonly LogEvent[];
  readonly state: BugReportState;
  /**
   * The session, in order, with times. Ruling 84.
   *
   * Its own channel for the reason the classifications have one: the event
   * log holds fifty entries, and a session that reaches eleven headings and
   * pauses twice would spend a fifth of them on moments and evict the
   * operational entries that explain a fault.
   */
  readonly timeline: readonly SessionMoment[];
}

/**
 * Builds the one-tap bug report: the last fifty entries, the current versioned
 * state, and the active file hash.
 *
 * **The file hash only.** Never the file, never its text. The hash identifies
 * which document was loaded without carrying any of it, which is what hard
 * rule 2 requires.
 */
export function buildBugReport(
  log: EventLog,
  state: BugReportState,
  capturedAt: string,
): BugReport {
  const classifications = log.classifications();
  const events = log.entries();
  const timeline = log.moments();
  return {
    schemaVersion: 3,
    capturedAt,
    held: {
      classifications: classifications.length,
      events: events.length,
      moments: timeline.length,
      momentsDropped: log.droppedMoments(),
    },
    classifications,
    events,
    state,
    timeline,
  };
}

/** File name for a saved report. Carries no user content. */
/**
 * What a classification decision records.
 *
 * Ruling 50: "for every classification decision the blank, its width, its
 * line, the rule that fired and the rule's reason". The blank's id is its
 * path through the document's own structure, which is shape and not text.
 */
export function classificationDetail(decision: {
  readonly blankId: string;
  readonly fieldType: string;
  readonly line: number;
  readonly reason: string;
  readonly rule: string;
  readonly width: number;
}): LogDetail {
  return {
    blank: decision.blankId,
    fieldType: decision.fieldType,
    line: decision.line,
    reason: decision.reason,
    rule: decision.rule,
    width: decision.width,
  };
}

export function bugReportFileName(capturedAt: string): string {
  return `atlas-bug-report-${capturedAt.replaceAll(/[^0-9A-Za-z]/g, "-")}.json`;
}

/**
 * Storage keys are namespaced and hashed, so listing them is safe. Any key
 * that does not match the application's own namespace is excluded rather than
 * reported, because it did not come from ATLas.
 */
/**
 * How many records of each type this device holds.
 *
 * This used to return the keys themselves, and that was a Ruling 50 leak
 * found in the trainee's own export of 8 October 2026: a key is
 * `r1:<recordType>:<identity>`, and the unresolved-token log builds its
 * identity as `<fileHash>:<token>` — where the token is an abbreviation
 * lifted straight out of his order set. Twelve such keys were in that
 * file, and the file is the one most likely to leave the device.
 *
 * A count per record type answers every question the keys answered — is
 * anything stored, how much, of what kind — and carries no identity at all.
 */
export function atlasStorageCounts(
  allKeys: readonly string[],
): Readonly<Record<string, number>> {
  const counts = new Map<string, number>();
  for (const key of allKeys) {
    const match = /^r1:([A-Za-z-]+):/.exec(key);
    if (match?.[1] === undefined) continue;
    counts.set(match[1], (counts.get(match[1]) ?? 0) + 1);
  }
  return Object.fromEntries([...counts.entries()].sort());
}

export function enabledFlagKeys(
  snapshot: FeatureFlagSnapshot,
): readonly FeatureFlagKey[] {
  return (Object.keys(snapshot) as FeatureFlagKey[]).filter(
    (key) => snapshot[key],
  );
}
